import { CONTENTS as RIM_IDS } from '~/assets/collections/rims';
import { devSimulatePing } from '~/rage/decorators';
import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import {
	IVehicleShopOffer,
	IVehicleTuning,
	IVehicleWheelsSettings,
	IVehicleWheelsSettingsRanges,
	VEHICLE_RIM_PAINT_TYPE_NAMES,
	vehicleRimPaintTypesOrder,
	VehicleTuningCategory,
	VehicleTuningSubcategory,
	VehicleWheelsSetting,
	VehicleWheelTireProfile,
	WalletType,
} from '~/shared';
import type {
	ICharacterOwnedFreeTuning,
	IVehicleTuningShopItem,
	IVehicleTuningShopService,
} from './vehicleTuningShopServiceTypes';

const rimDisplayName = (id: string): string =>
	id
		.replace(/^ccd_rim_/, '')
		.split('_')
		.join(' ')
		.toUpperCase();

const RIM_IDS_WITHOUT_IMAGE = new Set([
	'ccd_rim_3dsm_125_1pc',
	'ccd_rim_3dsm_305',
]);

const rimShopItems: IVehicleTuningShopItem[] = RIM_IDS.map((id, index) => {
	const unavailable = index % 4 === 3;
	const isInstalled = !unavailable && index % 5 === 1;
	const itemId = RIM_IDS_WITHOUT_IMAGE.has(id)
		? id.replace(/^ccd_rim_/, 'rims_')
		: id;

	return {
		id: itemId,
		categoryId: VehicleTuningCategory.RIMS,
		name: rimDisplayName(id),
		price: 50_000 + (index % 12) * 15_000,
		isInstalled,
		unavailable,
		paintType:
			vehicleRimPaintTypesOrder[index % vehicleRimPaintTypesOrder.length],
		variants: vehicleRimPaintTypesOrder.map((paintType, variantIndex) => ({
			id: `${itemId}_${paintType}`,
			name: `${rimDisplayName(id)} (${
				VEHICLE_RIM_PAINT_TYPE_NAMES[paintType]
			})`,
			isInstalled: isInstalled && variantIndex === 0,
		})),
	};
});

export class VehicleTuningShopServiceStub implements IVehicleTuningShopService {
	private readonly items = new Subject<IVehicleTuningShopItem[]>([
		{
			id: 'stock_front_bumper',
			categoryId: VehicleTuningCategory.FRONT_BUMPER,
			subcategoryId: VehicleTuningSubcategory.FRONT_BUMPER,
			name: 'Стоковый передний бампер',
			price: 0,
			isInstalled: true,
		},
		{
			id: 'test_item_2',
			categoryId: VehicleTuningCategory.FRONT_BUMPER,
			subcategoryId: VehicleTuningSubcategory.FRONT_BUMPER,
			name: 'Rocket Bunny SR II',
			price: 240000,
			isInstalled: false,
			variants: [
				{
					id: 'test_item_2_var_0',
					name: 'Матовый',
					price: 200_000,
					isInstalled: false,
				},
				{
					id: 'test_item_2_var_1',
					name: 'Глянец',
					price: 240_000,
					isInstalled: true,
				},
				{
					id: 'test_item_2_var_2',
					name: 'Карбон',
					price: 280_000,
					isInstalled: false,
				},
			],
		},
		{
			id: 'test_item_3',
			categoryId: VehicleTuningCategory.FRONT_BUMPER,
			subcategoryId: VehicleTuningSubcategory.FRONT_BUMPER,
			name: 'Test Bumper 123',
			price: 50000,
			isInstalled: false,
			unavailable: true,
		},
		{
			id: 'test_splitter_1',
			categoryId: VehicleTuningCategory.FRONT_BUMPER,
			subcategoryId: VehicleTuningSubcategory.SPLITTER,
			name: 'Сплиттер X',
			price: 30000,
			isInstalled: false,
		},
		{
			id: 'test_grille_1',
			categoryId: VehicleTuningCategory.FRONT_BUMPER,
			subcategoryId: VehicleTuningSubcategory.GRILLE,
			name: 'Решётка X',
			price: 18000,
			isInstalled: false,
		},
		// rear_bumper
		{
			id: 'stock_rear_bumper',
			categoryId: VehicleTuningCategory.REAR_BUMPER,
			subcategoryId: VehicleTuningSubcategory.REAR_BUMPER,
			name: 'Стоковый задний бампер',
			price: 0,
			isInstalled: false,
		},
		{
			id: 'test_item_5',
			categoryId: VehicleTuningCategory.REAR_BUMPER,
			subcategoryId: VehicleTuningSubcategory.REAR_BUMPER,
			name: 'Rear Bumber 1',
			price: 240000,
			isInstalled: false,
		},
		{
			id: 'test_item_6',
			categoryId: VehicleTuningCategory.REAR_BUMPER,
			subcategoryId: VehicleTuningSubcategory.REAR_BUMPER,
			name: 'Rear Bumber 2',
			price: 50000,
			isInstalled: true,
		},
		// wheels
		{
			id: 'stock_rims',
			categoryId: VehicleTuningCategory.RIMS,
			name: 'Стоковые диски',
			price: 0,
			isInstalled: false,
		},
		...rimShopItems,
		{
			id: 'stock_kit',
			name: 'Сток',
			categoryId: VehicleTuningCategory.KITS,
			price: 0,
			isInstalled: false,
		},
		{
			id: 'mod_kit_0_0',
			name: 'Набор #1',
			categoryId: VehicleTuningCategory.KITS,
			price: 10_000,
			isInstalled: true,
		},
		{
			id: 'mod_kit_0_1',
			name: 'Набор #2',
			categoryId: VehicleTuningCategory.KITS,
			price: 25_000,
			isInstalled: false,
		},
		{
			id: 'mod_kit_0_2',
			name: 'Набор #3',
			categoryId: VehicleTuningCategory.KITS,
			price: 80_000,
			isInstalled: false,
		},
		{
			id: 'mod_kit_0_3',
			name: 'Набор #4',
			categoryId: VehicleTuningCategory.KITS,
			price: 150_000,
			isInstalled: false,
		},
		{
			id: 'stock_spoiler',
			name: 'Стоковый спойлер',
			categoryId: VehicleTuningCategory.BOOT,
			subcategoryId: VehicleTuningSubcategory.BOOT_SPOILER,
			price: 0,
			isInstalled: true,
		},
	]);

	private shopOffer = new Subject<IVehicleShopOffer | null>({
		title: 'Новый привоз',
		description: 'Уникальный тюнинг в кейсе “Весна ‘25”',
		caseId: '123',
		color: '#01BBF3',
	});

	private readonly ownedWheelsSettings =
		new Subject<IVehicleWheelsSettings | null>({
			[VehicleWheelsSetting.FRONT_CAMBER]: 0,
			[VehicleWheelsSetting.REAR_CAMBER]: 0,
			[VehicleWheelsSetting.FRONT_OFFSET]: 0,
			[VehicleWheelsSetting.REAR_OFFSET]: 0,
			[VehicleWheelsSetting.RADIUS]: 1,
			[VehicleWheelsSetting.WIDTH]: 0.5,
		});
	private readonly wheelsSettingsRanges =
		new Subject<IVehicleWheelsSettingsRanges | null>({
			[VehicleWheelsSetting.FRONT_CAMBER]: [-45, 45],
			[VehicleWheelsSetting.REAR_CAMBER]: [-45, 45],
			[VehicleWheelsSetting.FRONT_OFFSET]: [-1, 1],
			[VehicleWheelsSetting.REAR_OFFSET]: [-1, 1],
			[VehicleWheelsSetting.RADIUS]: [0.75, 1.25],
			[VehicleWheelsSetting.WIDTH]: [0, 1],
		});

	private readonly wheelsSetupPrice = new Subject(200_000);
	private readonly tireProfileChangePrice = new Subject(50000);

	private readonly wheelTireProfile =
		new Subject<VehicleWheelTireProfile | null>(VehicleWheelTireProfile.MEDIUM);

	private readonly ownedFreeTuning = new Subject<ICharacterOwnedFreeTuning>({
		ownedItems: [],
		ownedKits: [],
	});

	private seenTracker = new SeenTracker(null);

	@devSimulatePing()
	public async buy(
		tuning: Partial<IVehicleTuning>,
		_wallet: WalletType,
	): Promise<void> {
		console.log('TuningShopServiceStub: buy', { tuning });

		if (tuning.items) {
			const items = this.items.getValue();
			items.forEach((item) => {
				item.isInstalled = tuning.items!.includes(item.id);
			});
		}
	}

	public getShopItems(): IObservable<IVehicleTuningShopItem[]> {
		return this.items;
	}

	public async setCameraFacing(
		shopCategory: VehicleTuningCategory | null,
		shopSubcategory?: VehicleTuningSubcategory | null,
	): Promise<void> {
		console.log('TuningShopServiceStub: setCameraFacing', {
			shopCategory,
			shopSubcategory,
		});
	}

	public getOwnedWheelsSettings(): IObservable<IVehicleWheelsSettings | null> {
		return this.ownedWheelsSettings;
	}

	public getWheelsSettingsRanges(): IObservable<IVehicleWheelsSettingsRanges | null> {
		return this.wheelsSettingsRanges;
	}

	public getWheelTireProfile(): IObservable<VehicleWheelTireProfile | null> {
		return this.wheelTireProfile;
	}

	public async buyWheelTireProfile(
		profile: VehicleWheelTireProfile,
	): Promise<void> {
		console.log('TuningShopServiceStub: buyWheelTireProfile', profile);
		this.wheelTireProfile.next(profile);
	}

	public getTireProfileChangePrice(): IObservable<number> {
		return this.tireProfileChangePrice;
	}

	public async setTiresProfilePreview(
		profile: VehicleWheelTireProfile,
	): Promise<void> {
		console.log('TuningShopServiceStub: setTiresProfilePreview', profile);
		this.wheelTireProfile.next(profile);
	}

	public async resetTiresProfilePreview(): Promise<void> {
		console.log('TuningShopServiceStub: resetTiresProfilePreview');
	}

	public async installItem(itemId: string | null): Promise<void> {
		// @TODO sdk api imitation
		console.log('TuningShopServiceStub: setItem', { itemId });
	}

	public async installItemsKit(itemsKitId: string | null): Promise<void> {
		// @TODO sdk api imitation
		console.log('TuningShopServiceStub: setItemsKit', { itemsKitId });
	}

	public async setWheelsSettings(
		wheelsSettings: Partial<IVehicleWheelsSettings>,
	): Promise<void> {
		// @TODO sdk api imitation
		console.log('TuningShopServiceStub: setWheelsSettings', wheelsSettings);
	}

	public async resetWheelsSettings(): Promise<void> {
		// @TODO
		console.log('TuningShopServiceStub: resetWheelsSettings');
	}

	public async removeTemporaryItems(): Promise<void> {
		// @TODO sdk api imitation ?
		console.log('TuningShopServiceStub: removeTemporaryItems');
	}

	public async resetTuning(): Promise<void> {
		console.log('TuningShopServiceStub: resetTuning');

		const items = this.items.getValue();
		items.forEach((item) => {
			item.isInstalled = item.id.startsWith('stock_');
			item.variants?.forEach((variant) => {
				variant.isInstalled = variant.id.startsWith('stock_');
			});
		});
		this.items.next([...items]);
	}

	public async fetchOwnedItemsSettings(): Promise<void> {
		// @TODO sdk api imitation ?
		console.log('TuningShopServiceStub: fetchOwnedItems');
	}

	public async exitMenu(): Promise<void> {
		console.log('TuningShopServiceStub: exit');
	}

	public getWheelsSetupPrice(): IObservable<number> {
		return this.wheelsSetupPrice;
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		console.log('TuningShopServiceStub: markSeen', path);
		this.seenTracker.markAsSeen(path);
	}

	public getShopOffer(): IObservable<IVehicleShopOffer | null> {
		return this.shopOffer;
	}

	public async installOwnedItem(itemId: string): Promise<void> {
		console.log('TuningShopServiceStub: installOwnedItem', { itemId });
	}

	public async installOwnedKit(kitId: string): Promise<void> {
		console.log('TuningShopServiceStub: installOwnedKit', { kitId });
	}

	public getIsOwned(itemId: string): boolean {
		return itemId === 'test_item_2' || itemId.startsWith('test_item_2_var_');
	}

	public async fetchOwnedFreeTuning(): Promise<void> {
		console.log('TuningShopServiceStub: fetchOwnedFreeTuning');
	}

	public getOwnedFreeTuning(): IObservable<ICharacterOwnedFreeTuning> {
		return this.ownedFreeTuning;
	}
}
